import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  ButtonGroup,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material';
import { ConfirmDialog } from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import {
  ReportType,
  ReportType as ExportType,
  ReportStatus,
  useGetReportFileDownloadLinkLazyQuery,
} from '@occc/graphql';
import { useAudienceExportMutation } from '@occc-app/dataAccessLayer/audience/mutations';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { DOWNLOAD_ERRORS, useS3Loader } from '@occc/feat-file-download';
import { createAudienceExportReportFileName } from '@occc-app/routes/Audience/AudienceExportReportPage/audience-export-report.utils';
import { messages } from './messages';

export type ExportOptionsDialogProps = {
  open: boolean;
  onClose: () => void;
  audienceId: string;
  audienceName: string;
  userEmail: string;
};

export const ExportOptionsDialog: React.FC<ExportOptionsDialogProps> = ({
  open,
  onClose,
  audienceId,
  audienceName,
  userEmail,
}) => {
  const [type, setType] = useState<ExportType>(ReportType.AudienceSnapshotWithoutPii);

  const { download } = useS3Loader({
    filename: createAudienceExportReportFileName({
      audienceName,
    }),
    onError: () => snackbar.error(DOWNLOAD_ERRORS.generic),
  });

  const [fetchDownloadLink, { loading: downloadLoading }] = useGetReportFileDownloadLinkLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const s3Url = data?.getReportFileDownloadLink;
      if (s3Url) {
        download(s3Url);
      } else {
        snackbar.error(messages.error);
      }
    },
    onError: () => snackbar.error(messages.error),
  });

  const [startExport, { loading }] = useAudienceExportMutation({
    variables: {
      input: {
        audienceUuid: audienceId,
        reportType: type,
        userEmails: [userEmail],
      },
    },
    onCompleted: (data) => {
      const result = data?.reportAudienceStartGenerationJob;

      if (!result) {
        snackbar.error(messages.error);
        onClose();
        return;
      }

      if (result.status === ReportStatus.Success && result.uuid) {
        fetchDownloadLink({ variables: { reportUuid: result.uuid } });
        snackbar.success(messages.downloadSuccess);
      } else if (result.status === ReportStatus.Pending) {
        snackbar.success(messages.downloadPending);
      } else {
        snackbar.error(messages.error);
      }

      onClose();
    },
    onError: () => {
      snackbar.error(messages.error);
    },
  });

  const showPIICaution = type === ReportType.AudienceSnapshotWithPii;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      dialogTitle={messages.dialogTitle}
      confirmButtonText={messages.exportButton}
      confirmButtonProps={{
        onClick: () => startExport(),
        color: 'primary',
        disabled: loading || downloadLoading,
      }}
    >
      <ButtonGroup sx={{ mt: 1, mb: 2 }}>
        <Button
          onClick={() => setType(ReportType.AudienceSnapshotWithoutPii)}
          variant={type === ReportType.AudienceSnapshotWithoutPii ? 'contained' : 'outlined'}
          sx={{ py: 2.3, px: 5 }}
        >
          {messages.exportNonPII}
        </Button>
        <Button
          onClick={() => setType(ReportType.AudienceSnapshotWithPii)}
          variant={type === ReportType.AudienceSnapshotWithPii ? 'contained' : 'outlined'}
          sx={{ py: 2.3, px: 5 }}
        >
          {messages.exportAllPII}
        </Button>
      </ButtonGroup>

      {showPIICaution && (
        <Box sx={{ width: 400 }}>
          <Alert severity='info'>
            <AlertTitle>{messages.piiCautionTitle}</AlertTitle>
            <Typography variant='body2'>{messages.piiCautionText}</Typography>
          </Alert>
        </Box>
      )}

      {(loading || downloadLoading) && (
        <Box mt={2} display='flex' alignItems='center' gap={1}>
          <CircularProgress size={18} />
          <Typography variant='body2'>{messages.loadingExportMessage}</Typography>
        </Box>
      )}
    </ConfirmDialog>
  );
};
